import React, { useState, useEffect } from "react";
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
} from "@mui/material";
import {
    ArrowBackIos,
    ArrowForwardIos,
    Group as GroupIcon,
    Groups as GroupsIcon,
    Edit as EditIcon,
    Alarm as AlarmIcon,
    AccessTimeFilled as AccessTimeFilledIcon,
    PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { getWaitingCustomerCount } from "./../../api/api";

const PanelContent = () => {
    const [faqs, setFaqs] = useState({});
    const [selectedDate, setSelectedDate] = useState(dayjs()); // today's date

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getWaitingCustomerCount(selectedDate);
                console.log("Fetched data:", data);
                setFaqs(data);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [selectedDate]);

    const handlePreviousDay = () => {
        setSelectedDate((prevDate) => prevDate.subtract(1, "day"));
    };

    const handleNextDay = () => {
        const today = dayjs().startOf('day');
        setSelectedDate((prevDate) => {
            const nextDate = prevDate.add(1, "day");
            const nextDateStr = nextDate.format('YYYY-MM-DD');
            const todayStr = today.format('YYYY-MM-DD');
            // Don't allow going to future dates - only allow up to today
            if (nextDateStr > todayStr) {
                return prevDate; // Return current date if next would be in future
            }
            return nextDate; // Allow going to today from yesterday
        });
    };

    // Check if next day button should be disabled
    // Disable only if selected date is today (can't go to future)
    const today = dayjs().startOf('day');
    const selectedDateStart = selectedDate.startOf('day');
    // Compare dates using format strings - disable if selected date is today or later
    const selectedDateStr = selectedDateStart.format('YYYY-MM-DD');
    const todayStr = today.format('YYYY-MM-DD');
    const isNextDayDisabled = selectedDateStr >= todayStr;

    const cardData = [
        {
            title: "Current Waiting Customers",
            value: faqs.waiting_count,
            icon: <GroupIcon />,
        },
        {
            title: "Total Number of Customers",
            value: faqs.total_count,
            icon: <GroupsIcon />,
        },
        {
            title: "Number of Self-Registered Customers",
            value: faqs.self_reg_count,
            icon: <PersonAddIcon />,
        },
        {
            title: "Number of Direct Assisted Customers",
            value: faqs.direct_reg_count,
            icon: <PersonAddIcon />,
        },
        {
            title: "Number of Manually Registered Customers",
            value: faqs.manual_reg_count,
            icon: <EditIcon />,
        },
        {
            title: "Number of Lost Customers",
            value: faqs.lost_count,
            icon: <EditIcon />,
        },
        {
            title: "Average Wait Time",
            value: faqs.average_wait_time,
            icon: <AccessTimeFilledIcon />,
        },
        {
            title: "Longest Wait Time",
            value: faqs.longest_wait_time,
            icon: <AlarmIcon />,
        },
        {
            title: "Maximum Waiting Queue",
            value: faqs.max_wait,
            icon: <EditIcon />,
        },
    ];

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Grid container spacing={3} alignItems="stretch">
                {cardData.map((card, index) => (
                    <Grid item key={index} xs={12} sm={6} md={3} sx={{ mb: 3 }}>
                        <Card
                            sx={{
                                backgroundColor: "#00acc1",
                                color: "white",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2,
                                boxShadow: 3,
                                height: "100%",
                            }}
                        >
                            <CardContent sx={{ p: 0, flexGrow: 1 }}>
                                <Typography
                                    variant="h3"
                                    sx={{ fontWeight: "bold", mb: 1 }}
                                >
                                    {card.value ?? "-"}
                                </Typography>
                                <Typography variant="body1">
                                    {card.title}
                                </Typography>
                            </CardContent>
                            <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                {React.cloneElement(card.icon, {
                                    sx: {
                                        fontSize: "5rem",
                                        color: "black",
                                        opacity: 0.2,
                                    },
                                })}
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Navigation Controls */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 4,
                }}
            >
                <IconButton
                    onClick={handlePreviousDay}
                    sx={{ color: "#00acc1", mx: 2 }}
                >
                    <ArrowBackIos />
                </IconButton>

                <Typography variant="h6">
                    {selectedDate.format("YYYY-MM-DD")}
                </Typography>

                <IconButton
                    onClick={handleNextDay}
                    disabled={isNextDayDisabled}
                    sx={{ 
                        color: "#00acc1", 
                        mx: 2,
                        "&.Mui-disabled": {
                            color: "#bdbdbd"
                        }
                    }}
                >
                    <ArrowForwardIos />
                </IconButton>
            </Box>
        </Box>
    );
};

export default PanelContent;
